import React, { useEffect, useRef, useState, useMemo } from "react";
import { CircularProgress } from "@material-ui/core";
import { apiReports } from "../api/reports";
import { config } from "../core";
import { loadGoogle } from "./InspectorRouteMap";

// Verdict scale, good → bad. An area is coloured by whichever bucket of its open
// backlog dominates (see area drill-down "Open backlog" panel for the same logic).
const VERDICT_COLORS = {
  healthy: "#94ba33", // future appts dominate — scheduled ahead
  inFlight: "#a3d977", // in-progress dominates — being worked
  schedulingGap: "#fbbf24", // not-started dominates — undated work piling up
  slipping: "#f97316", // overdue 8-30d dominates
  behind: "#ef4444", // overdue ≤7d dominates — slipping now
  stale: "#7f1d1d", // overdue >30d dominates — stuck old work
  clear: "#d1d5db", // no open backlog
};
const VERDICT_LABELS = {
  healthy: "Healthy",
  inFlight: "In flight",
  schedulingGap: "Scheduling gap",
  slipping: "Slipping",
  behind: "Behind this week",
  stale: "Stale pile",
  clear: "Clear",
};
const VERDICT_ORDER = ["healthy", "inFlight", "schedulingGap", "slipping", "behind", "stale"];

// dominant bucket → verdict key (mirrors AreaUtilisationDetailModal OpenBacklog)
const verdictFor = (o) => {
  if (!o || !o.total) { return "clear"; }
  const parts = [
    ["future", o.future || 0],
    ["inProgress", o.inProgress || 0],
    ["notStarted", o.notStarted || 0],
    ["overdue7", o.overdue7 || 0],
    ["overdue30", o.overdue30 || 0],
    ["overdueStale", o.overdueStale || 0],
  ];
  const dom = parts.sort((a, b) => b[1] - a[1])[0];
  return {
    future: "healthy", inProgress: "inFlight", notStarted: "schedulingGap",
    overdue7: "behind", overdue30: "slipping", overdueStale: "stale",
  }[dom[0]];
};

const countyKey = (state, county) => `${state}|${county}`.toLowerCase().replace(/[^a-z0-9]/g, "");

const HealthMapTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [days] = useState(30);

  const mapDivRef = useRef(null);
  const [google, setGoogle] = useState(null);
  const [map, setMap] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  const dataLayerRef = useRef(null);
  const infoWindowRef = useRef(null);
  const lastFitRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    apiReports.getHealthMap(days).then((res) => {
      if (cancelled) { return; }
      const r = res?.data?.result;
      if (res?.data?.status === 200 && r) { setData(r); }
      else { setError(res?.data?.message || "Failed to load health map."); }
      setLoading(false);
    }).catch(() => { if (!cancelled) { setError("Failed to load health map."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [days]);

  // countyKey → { area, verdict }
  const countyToArea = useMemo(() => {
    const m = new Map();
    (data?.areas || []).forEach((a) => {
      const v = verdictFor(a.open);
      (a.counties || []).forEach((c) => m.set(countyKey(c.state, c.county), { area: a, verdict: v }));
    });
    return m;
  }, [data]);

  // init the map once
  useEffect(() => {
    let cancelled = false;
    loadGoogle().then((g) => {
      if (cancelled) { return; }
      setGoogle(g);
      const m = new g.maps.Map(mapDivRef.current, {
        center: { lat: 28, lng: -82 },
        zoom: 5,
        mapTypeControl: false,
        streetViewControl: false,
        scaleControl: true,
      });
      setMap(m);
      infoWindowRef.current = new g.maps.InfoWindow();
      setMapLoading(false);
    }).catch(() => { if (!cancelled) { setMapError("Failed to load map."); setMapLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // render county polygons whenever data arrives
  useEffect(() => {
    if (!map || !google || !data) { return; }
    let cancelled = false;
    fetch(`${config.staticPath}/geo/counties.geojson`)
      .then((res) => res.json())
      .then((geoJson) => {
        if (cancelled) { return; }
        if (dataLayerRef.current) { dataLayerRef.current.setMap(null); dataLayerRef.current = null; }
        const dataLayer = new google.maps.Data();
        const bounds = new google.maps.LatLngBounds();
        geoJson.features.forEach((feature) => {
          const p = feature.properties;
          const key = countyKey(p.state, p.NAME);
          const entry = countyToArea.get(key);
          if (!entry) { return; } // only counties in this company's areas
          const clone = JSON.parse(JSON.stringify(feature));
          const added = dataLayer.addGeoJson(clone);
          added.forEach((f) => {
            const o = entry.area.open || {};
            f.setProperty("verdict", entry.verdict);
            f.setProperty("areaName", entry.area.name);
            f.setProperty("total", o.total || 0);
            f.setProperty("notStarted", o.notStarted || 0);
            f.setProperty("inProgress", o.inProgress || 0);
            f.setProperty("overdue", o.overdue || 0);
            f.setProperty("overdue7", o.overdue7 || 0);
            f.setProperty("overdue30", o.overdue30 || 0);
            f.setProperty("overdueStale", o.overdueStale || 0);
            f.setProperty("future", o.future || 0);
            f.setProperty("countyName", p.NAME);
            f.setProperty("state", p.state);
            f.getGeometry().forEachLatLng((ll) => bounds.extend(ll));
          });
        });
        dataLayer.setStyle((f) => ({
          fillColor: VERDICT_COLORS[f.getProperty("verdict")] || "#e5e7eb",
          fillOpacity: 0.75,
          strokeColor: "#374151",
          strokeWeight: 1,
        }));
        dataLayer.addListener("click", (event) => {
          const f = event.feature;
          const v = f.getProperty("verdict");
          infoWindowRef.current.setContent(
            `<div style="min-width:180px">` +
              `<div style="font-weight:700;font-size:14px">${f.getProperty("areaName") || ""}</div>` +
              `<div style="margin-top:2px;font-size:12px;color:#fff;background:${VERDICT_COLORS[v] || "#888"};display:inline-block;padding:1px 6px;border-radius:4px;font-weight:600">${VERDICT_LABELS[v] || ""}</div>` +
              `<div style="margin-top:6px;font-size:12px;color:#374151">` +
                `Open: <b>${f.getProperty("total")}</b><br>` +
                `Not started: ${f.getProperty("notStarted")} · In progress: ${f.getProperty("inProgress")}<br>` +
                `Overdue: <b>${f.getProperty("overdue")}</b> (≤7d ${f.getProperty("overdue7")} · 8-30d ${f.getProperty("overdue30")} · &gt;30d ${f.getProperty("overdueStale")})<br>` +
                `Future appts: ${f.getProperty("future")}` +
              `</div>` +
              `<div style="margin-top:4px;font-size:11px;color:#6b7280">${f.getProperty("countyName")}, ${f.getProperty("state")}</div>` +
            `</div>`
          );
          infoWindowRef.current.setPosition(event.latLng);
          infoWindowRef.current.open(map);
        });
        dataLayer.setMap(map);
        dataLayerRef.current = dataLayer;
        if (!bounds.isEmpty()) {
          const sw = bounds.getSouthWest().toUrlValue(6);
          const ne = bounds.getNorthEast().toUrlValue(6);
          const sig = `${sw}|${ne}`;
          if (sig !== lastFitRef.current) { map.fitBounds(bounds, 24); lastFitRef.current = sig; }
        }
      })
      .catch(() => { if (!cancelled) { setMapError("Failed to load county boundaries."); } });
    return () => {
      cancelled = true;
      if (dataLayerRef.current) { dataLayerRef.current.setMap(null); dataLayerRef.current = null; }
      if (infoWindowRef.current) { infoWindowRef.current.close(); }
    };
  }, [map, google, data, countyToArea]);

  const areas = data?.areas || [];
  const tally = useMemo(() => {
    const t = {};
    areas.forEach((a) => { const v = verdictFor(a.open); t[v] = (t[v] || 0) + 1; });
    return t;
  }, [areas]);

  return (
    <div className="tw-space-y-4">
      <div>
        <h2 className="tw-text-xl tw-font-semibold">Health Map</h2>
        <p className="tw-text-sm tw-text-gray-600 tw-mt-1">
          Each area shaded by where its open backlog sits — green is keeping up, red is slipping or stuck.
          Helps answer “do we need more inspectors, or is there not enough work?” without reading a table.
        </p>
      </div>

      {loading && <div className="tw-flex tw-justify-center tw-py-10"><CircularProgress /></div>}
      {error && <div className="tw-text-fs-red tw-py-6 tw-text-center">{error}</div>}

      {/* Map — always rendered so the ref exists when the init effect runs on mount */}
      <div className="tw-relative tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden" style={{ height: 480 }}>
        <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />
        {mapLoading && 
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white tw-bg-opacity-60">
            <CircularProgress />
          </div>
        }
        {mapError && 
          <div className="tw-absolute tw-top-2 tw-left-2 tw-text-red-600 tw-text-sm tw-bg-white tw-px-2 tw-py-1 tw-rounded">
            {mapError}
          </div>
        }
      </div>

      {data && 
        <>
          {/* Legend */}
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-bg-gray-50 tw-rounded-lg tw-p-2">
            <span className="tw-text-xs tw-text-gray-500 tw-mr-1">Good</span>
            {VERDICT_ORDER.map((v) => 
              <div key={v} className="tw-flex tw-items-center tw-gap-1.5">
                <span style={{ background: VERDICT_COLORS[v], width: 14, height: 14, display: "inline-block", borderRadius: 3 }} />
                <span className="tw-text-xs tw-text-gray-700">{VERDICT_LABELS[v]}</span>
                {tally[v] > 0 && <span className="tw-text-xs tw-text-gray-400">({tally[v]})</span>}
              </div>
            )}
            <span className="tw-text-xs tw-text-gray-500 tw-ml-1">Bad</span>
          </div>

          {/* Area summary list */}
          <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
            <table className="tw-min-w-full tw-text-sm">
              <thead className="tw-bg-gray-50">
                <tr className="tw-border-b tw-border-gray-200">
                  <th className="tw-px-3 tw-py-2 tw-text-left">Area</th>
                  <th className="tw-px-3 tw-py-2 tw-text-left">Status</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right">Open</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right">Not started</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right">In progress</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right">Overdue</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right">Future</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right">Arrivals ({days}d)</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right">Done ({days}d)</th>
                </tr>
              </thead>
              <tbody>
                {areas.map((a) => {
                  const v = verdictFor(a.open);
                  const o = a.open || {};
                  return (
                    <tr key={a.workAreaId} className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-3 tw-py-2 tw-font-medium">{a.name}</td>
                      <td className="tw-px-3 tw-py-2">
                        <span style={{ background: VERDICT_COLORS[v] }} className="tw-text-white tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold">
                          {VERDICT_LABELS[v]}
                        </span>
                      </td>
                      <td className="tw-px-3 tw-py-2 tw-text-right">{o.total}</td>
                      <td className="tw-px-3 tw-py-2 tw-text-right">{o.notStarted}</td>
                      <td className="tw-px-3 tw-py-2 tw-text-right">{o.inProgress}</td>
                      <td className="tw-px-3 tw-py-2 tw-text-right tw-text-fs-red">{o.overdue}</td>
                      <td className="tw-px-3 tw-py-2 tw-text-right">{o.future}</td>
                      <td className="tw-px-3 tw-py-2 tw-text-right">{a.incoming}</td>
                      <td className="tw-px-3 tw-py-2 tw-text-right">{a.complete}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="tw-text-xs tw-text-gray-500">
            Open = work not yet finished. Not started = no appointment set. In progress = being worked.
            Overdue = appointment date passed and not done. Future = appointment still to come.
          </p>
        </>
      }
    </div>
  );
};

export default HealthMapTab;